import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { partners } from '../data/mock';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Partners = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-dark-bg">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold text-xs font-medium uppercase tracking-widest mb-4">
            Building Together
          </p>
          
          <h1 className="text-4xl md:text-5xl font-bold text-warm-white mb-6 tracking-tight">
            Our Partners & Sponsors
          </h1>
          
          <p className="text-lg text-warm-white/60 max-w-2xl mx-auto leading-relaxed">
            We're grateful to partner with organizations that share our mission
            of making learning accessible, engaging, and transformative.
          </p>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partners.map((partner) => (
              <Card
                key={partner.id}
                className="group border border-dark-border bg-dark-card hover:border-gold/50 transition-all duration-300 cursor-pointer"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gold flex items-center justify-center mx-auto mb-6 group-hover:scale-105 transition-transform duration-300">
                    <span className="text-2xl font-bold text-dark-bg">
                      {partner.logo}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-warm-white mb-2 group-hover:text-gold transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-warm-white/60 text-sm mb-4">
                    {partner.description}
                  </p>
                  <a
                    href={partner.url}
                    className="inline-flex items-center text-gold hover:text-gold/80 text-xs font-medium uppercase tracking-wider"
                  >
                    Learn More
                    <ExternalLink className="ml-2 w-3 h-3" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-dark-card border-y border-dark-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-white mb-10 text-center tracking-tight">
            Why Partner With Us?
          </h2>

          <div className="space-y-6 text-warm-white/70 leading-relaxed mb-16">
            <p>
              Monday Morning Learning Podcasts reaches a highly engaged audience
              of professionals, lifelong learners, and decision-makers who value
              quality content and meaningful growth.
            </p>
            <p>
              Our listeners are actively seeking tools, resources, and services
              that can help them learn better, work smarter, and live more
              intentionally. They trust our recommendations and are eager to
              discover solutions that align with their values.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="p-6 text-center border border-dark-border bg-dark-bg">
              <div className="text-4xl font-bold text-gold mb-2">10K+</div>
              <p className="text-warm-white/60 text-xs uppercase tracking-widest">Monthly Listeners</p>
            </Card>
            <Card className="p-6 text-center border border-dark-border bg-dark-bg">
              <div className="text-4xl font-bold text-gold mb-2">25+</div>
              <p className="text-warm-white/60 text-xs uppercase tracking-widest">Countries Reached</p>
            </Card>
            <Card className="p-6 text-center border border-dark-border bg-dark-bg">
              <div className="text-4xl font-bold text-gold mb-2">4.9</div>
              <p className="text-warm-white/60 text-xs uppercase tracking-widest">Average Rating</p>
            </Card>
          </div>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-warm-white mb-4 tracking-tight">
              Partnership Opportunities
            </h3>
            <p className="text-warm-white/60 mb-10 max-w-2xl mx-auto">
              We offer flexible sponsorship packages including episode
              sponsorships, newsletter mentions, and custom content
              collaborations.
            </p>
            <Link to="/contact">
              <Button className="bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-sm uppercase tracking-wider px-10 py-4 transition-all duration-300">
                Become a Partner
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-white mb-16 text-center tracking-tight">
            What We Look For in Partners
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6">
                <span className="text-dark-bg font-bold">01</span>
              </div>
              <h3 className="text-xl font-bold text-warm-white mb-3">
                Aligned Mission
              </h3>
              <p className="text-warm-white/60 leading-relaxed text-sm">
                Partners who share our commitment to education, growth, and
                making a positive impact.
              </p>
            </Card>

            <Card className="p-8 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6">
                <span className="text-dark-bg font-bold">02</span>
              </div>
              <h3 className="text-xl font-bold text-warm-white mb-3">
                Quality Products
              </h3>
              <p className="text-warm-white/60 leading-relaxed text-sm">
                We only recommend products and services we genuinely believe in
                and would use ourselves.
              </p>
            </Card>

            <Card className="p-8 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6">
                <span className="text-dark-bg font-bold">03</span>
              </div>
              <h3 className="text-xl font-bold text-warm-white mb-3">
                Mutual Value
              </h3>
              <p className="text-warm-white/60 leading-relaxed text-sm">
                Partnerships that create genuine value for our audience and your
                community.
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
